import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Construct mailto link
    const subject = encodeURIComponent('Demande de visite - Villa Croissy');
    const body = encodeURIComponent(
      `Nom: ${formData.name}\nEmail: ${formData.email}\nTéléphone: ${formData.phone}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:villacroissy@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section 
      id="contact"
      className="py-24 md:py-32 contact-bg"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl text-white font-['Playfair_Display'] mb-6"
              data-testid="contact-title"
            >
              {t('contact_title')}
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-10 max-w-lg leading-relaxed">
              {t('contact_subtitle')}
            </p>

            {/* Direct Contact */}
            <div className="space-y-6">
              <p className="text-[#C5A059] text-sm uppercase tracking-[0.2em]">
                {t('contact_direct')}
              </p>
              
              <a 
                href="mailto:villacroissy@gmail.com" 
                className="flex items-center gap-4 text-white hover:text-[#C5A059] transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center group-hover:border-[#C5A059] transition-colors">
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <span className="text-lg">villacroissy@gmail.com</span>
              </a>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-white/50 text-sm mb-6">{t('contact_or')}</p>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <Input
                type="text"
                name="name"
                placeholder={t('contact_name')}
                value={formData.name}
                onChange={handleChange}
                required
                className="input-luxury h-14 px-5 rounded-none"
                data-testid="contact-name-input"
              />
              
              <Input
                type="email"
                name="email"
                placeholder={t('contact_email')}
                value={formData.email}
                onChange={handleChange}
                required
                className="input-luxury h-14 px-5 rounded-none"
                data-testid="contact-email-input"
              />
              
              <Input
                type="tel"
                name="phone"
                placeholder={t('contact_phone')}
                value={formData.phone}
                onChange={handleChange}
                className="input-luxury h-14 px-5 rounded-none"
                data-testid="contact-phone-input"
              />
              
              <Textarea
                name="message"
                placeholder={t('contact_message')}
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="input-luxury px-5 py-4 rounded-none resize-none"
                data-testid="contact-message-input"
              />
              
              <Button
                type="submit"
                className="w-full h-14 bg-[#C5A059] hover:bg-[#D4B06A] text-white rounded-none font-medium tracking-wide flex items-center justify-center gap-3"
                data-testid="contact-submit-btn"
              >
                <span>{submitted ? '✓' : t('contact_send')}</span>
                {!submitted && <Send className="w-4 h-4" strokeWidth={1.5} />}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
